import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Users, Briefcase, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ChatRoomList = ({ rooms, openRoom, setShowNewRoom }) => {
  // Parse room name into title and site
  const parseRoomName = (name) => {
    if (!name) return { title: 'Unknown', site: null };
    // Format: "Job #25 - Customer Name - Site Name"
    const parts = name.split(' - ');
    if (parts.length >= 3) {
      // Job # and Customer on first line, Site on second
      return { 
        title: `${parts[0]} - ${parts[1]}`, 
        site: parts.slice(2).join(' - ') 
      };
    } else if (parts.length === 2) {
      return { title: parts[0], site: parts[1] };
    }
    return { title: name, site: null };
  };

  return (
    <ScrollArea className="h-[calc(100vh-3.5rem)]">
      {rooms.length === 0 ? (
        <div className="text-center text-muted-foreground py-12 px-4">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No conversations yet</p>
          <p className="text-sm mt-1">Create a chat room or open a job to start messaging</p>
          <Button
            className="mt-4"
            onClick={() => setShowNewRoom(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Chat Room
          </Button>
        </div>
      ) : (
        <div className="divide-y">
          {rooms.map((room) => {
            const { title, site } = parseRoomName(room.name);
            const hasUnread = room.unread_count > 0;
            
            return (
              <button
                key={room.id}
                onClick={() => openRoom(room)}
                className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${hasUnread ? 'bg-muted/30' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <div className={`p-2 rounded-full ${
                      room.room_type === 'job' ? 'bg-blue-500/10' : 'bg-[#ffd108]/20'
                    }`}>
                      {room.room_type === 'job' ? (
                        <Briefcase className="h-5 w-5 text-blue-500" />
                      ) : (
                        <Users className="h-5 w-5 text-[#ffd108]" />
                      )}
                    </div>
                    {hasUnread && (
                      <span className="absolute -top-1 -right-1 bg-[#ffd108] text-black text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                        {room.unread_count > 9 ? '9+' : room.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className={`font-medium truncate ${hasUnread ? 'text-foreground' : ''}`}>{title}</h4>
                      {room.last_message_at && (
                        <span className="text-xs text-muted-foreground ml-2 shrink-0">
                          {formatDistanceToNow(new Date(room.last_message_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    {site && (
                      <p className="text-xs text-muted-foreground truncate">{site}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {room.room_type === 'job' ? 'Job Chat' : 'General'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {room.participants?.length || 0} members
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </ScrollArea>
  );
};

export default ChatRoomList;
